import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "@helpers/translate";
import { PublicFeedTable } from "./PublicFeedTable";
import { LeaderboardTable } from "./LeaderboardTable";
import { BestTradesTable } from "./BestTradesTable";
import { CommunityDateRange } from "./CommunityDateRange";
import {
  TradeDetailsModal,
  TraderProfileModal,
  fromCommunityTrade,
} from "../../TradeModals";
import {
  COMMUNITY_TIME_RANGES,
  communityService,
} from "./communityService";

const RANGE_TABS = new Set(["leaderboard", "best-trades"]);

export const CommunitySection = ({ activeTab, onTabChange }) => {
  const { __ } = useTranslation();

  const [currentTab, setCurrentTab] = useState(activeTab || "public-feed");
  const [timeRange, setTimeRange] = useState(COMMUNITY_TIME_RANGES.ONE_DAY);
  const [publicFeedData, setPublicFeedData] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [bestTradesData, setBestTradesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tradeDetails, setTradeDetails] = useState(null);
  const [trader, setTrader] = useState(null);

  const openTraderProfile = (username) => {
    communityService
      .getTraderProfile(username)
      .then(setTrader)
      .catch(() => {});
  };

  useEffect(() => {
    if (activeTab && activeTab !== currentTab) {
      setCurrentTab(activeTab);
    }
  }, [activeTab]);

  const handleTabClick = (tabId) => {
    setCurrentTab(tabId);
    onTabChange?.(tabId);
  };

  const showDateRange = RANGE_TABS.has(currentTab);

  // Public feed loads once; ranged tables refetch when range changes
  useEffect(() => {
    let isMounted = true;

    communityService
      .getPublicFeed()
      .then((feed) => {
        if (isMounted) setPublicFeedData(feed);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!showDateRange) {
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    setLoading(true);

    const load =
      currentTab === "leaderboard"
        ? communityService.getLeaderboard({ range: timeRange })
        : communityService.getBestTrades({ range: timeRange });

    load
      .then((rows) => {
        if (!isMounted) return;
        if (currentTab === "leaderboard") {
          setLeaderboardData(rows);
        } else {
          setBestTradesData(rows);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [currentTab, timeRange, showDateRange]);

  const tabs = [
    { id: "public-feed", label: __("futures.community.public_feed") },
    { id: "leaderboard", label: __("futures.community.leaderboard") },
    { id: "best-trades", label: __("futures.community.best_trades") },
  ];

  return (
    <section className="futuresOrders__section futuresOrders__section--community">
      <h2 className="futuresOrders__title">{__("futures.community")}</h2>

      <div className="futuresOrders__head">
        <div
          className="futuresOrders__head-left"
          style={{ position: "relative" }}
        >
          <ul className="futuresOrders__tabs">
            {tabs.map((tab) => (
              <li key={tab.id} className="futuresOrders__tab-item">
                <button
                  type="button"
                  className={`futuresOrders__tab ${
                    currentTab === tab.id ? "futuresOrders__tab--active" : ""
                  }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {showDateRange && (
          <div className="futuresOrders__head-right">
            <CommunityDateRange
              value={timeRange}
              onChange={setTimeRange}
              disabled={loading}
            />
          </div>
        )}
      </div>

      <div className="futuresOrders__table-wrapper community-table-wrapper--public-feed community-table-wrapper--community">
        {currentTab === "public-feed" && (
          <PublicFeedTable
            data={publicFeedData}
            loading={loading}
            onRowClick={(row) => setTradeDetails(fromCommunityTrade(row))}
          />
        )}
        {currentTab === "leaderboard" && (
          <LeaderboardTable
            data={leaderboardData}
            loading={loading}
            onRowClick={(row) => openTraderProfile(row.user)}
          />
        )}
        {currentTab === "best-trades" && (
          <BestTradesTable
            data={bestTradesData}
            loading={loading}
            onRowClick={(row) => setTradeDetails(fromCommunityTrade(row))}
          />
        )}
      </div>

      <TradeDetailsModal
        trade={tradeDetails}
        onClose={() => setTradeDetails(null)}
        onUserClick={(user) => {
          setTradeDetails(null);
          openTraderProfile(user);
        }}
      />

      <TraderProfileModal
        trader={trader}
        onClose={() => setTrader(null)}
        onTradeClick={(trade) => {
          // Open trades of a profile don't carry the owner, it is the trader
          setTradeDetails(fromCommunityTrade({ ...trade, user: trader.user }));
          setTrader(null);
        }}
      />
    </section>
  );
};

CommunitySection.propTypes = {
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
};

export default CommunitySection;
